import React, { useRef, useState, useEffect } from "react";
import groupByConsecutiveSpeakers from "../../../../services/GroupSegments";
import SegmentGroup from "./SegmentGroup";
import styles from "./styles.module.scss";
import SegmentPopup from "./SegmentPopup";
import { useDispatch, useSelector } from "react-redux";
import { updateDataset } from "../../../../redux/dataset/datasetSlice";
import swal from "sweetalert";
import TranscriptModerator from "../TranscriptModerator";
import SpeakerList from "../SpeakerList";
import ExtraButtons from "../ExtraButtons";
import ScrollToTop from "../ScrollToTop";
import hasEmptySpeaker from "../../../../services/CheckEmptySpeakers";
import ExportTranscript from "../ExportTranscript";

function Transcript() {
  const data = useSelector((state) => state.datasetSlice.dataset);
  const headerHeight = useSelector((state) => state.headerSlice.height);
  const stickyAsideRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [selectedRange, setSelectedRange] = useState({
    start: null,
    end: null,
  });
  const dispatch = useDispatch();
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState(null);
  const stopEditing = data.transcript_status === "COMPLETED" ? true : false;
  const mediaRef = useRef(null);
  const isVideo = /^(mp4|webm|ogg)$/i.test(data.file_type);

  const [showSearchReplace, setShowSearchReplace] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const searchReplaceRef = useRef(null);

  const handleSearchReplace = () => {
    if (!searchText) return;

    swal({
      title: "Confirm Replace All",
      text: `Replace all (${data.transcript.segments.filter(segment => segment.text.includes(searchText)).length}) occurrences of "${searchText}" with "${replaceText}"?`,
      icon: "warning",
      buttons: ["Cancel", "Replace All"],
      dangerMode: true,
    }).then((willReplace) => {
      if (willReplace) {
        const newSegments = data.transcript.segments.map(segment => ({
          ...segment,
          text: segment.text.replaceAll(searchText, replaceText)
        }));

        const newRawText = newSegments.map(segment => segment.text).join("");

        dispatch(
          updateDataset({
            transcript: { text: newRawText, segments: newSegments },
          })
        );

        setShowSearchReplace(false);
        setSearchText('');
        setReplaceText('');
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchReplaceRef.current && !searchReplaceRef.current.contains(event.target)) {
        setShowSearchReplace(false);
        setSearchText('');
        setReplaceText('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const mediaElement = document.getElementById("mediaTranscript");

    const handleTimeUpdate = () => {
      const playbackTime = mediaElement.currentTime;

      const playingSegment = data.transcript.segments.find(
        (segment) =>
          playbackTime >= segment.start && playbackTime <= segment.end
      );

      if (playingSegment) {
        setCurrentPlayingSegment(playingSegment.id);
      } else {
        setCurrentPlayingSegment(null);
      }
    };

    mediaElement.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      mediaElement.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [data.transcript.segments]);

  useEffect(() => {
    if (stickyAsideRef.current) {
      stickyAsideRef.current.style.top = `${headerHeight + 24}px`;
    }
  }, [headerHeight]);

  const handleSegmentClick = (segment, event) => {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    if (!selectedRange.start) {
      setSelectedRange({ start: segment, end: null });
    } else if (selectedRange.start && !selectedRange.end) {
      const startSegmentGroup = groupedSegments.find((group) =>
        group.includes(selectedRange.start)
      );
      const endSegmentGroup = groupedSegments.find((group) =>
        group.includes(segment)
      );

      if (startSegmentGroup === endSegmentGroup) {
        const startIndex = startSegmentGroup.indexOf(selectedRange.start);
        const endIndex = startSegmentGroup.indexOf(segment);
        if (endIndex > startIndex) {
          setSelectedRange((prevState) => ({ ...prevState, end: segment }));
        } else {
          return;
        }
      } else {
        setSelectedRange({ start: segment, end: null });
      }
    } else {
      setSelectedRange({ start: segment, end: null });
    }

    if (currentSegment === segment) {
      setCurrentSegment(null);
      setSelectedRange({ start: null, end: null });
    } else {
      setCurrentSegment(segment);
    }
  };

  const handleEditClick = (segment) => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentSegment(null);
    setSelectedRange({ start: null, end: null });
  };

  const handleSegmentDoubleClick = (startTime) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = startTime;
      mediaRef.current.play();
    }
  };

  const handleSaveSegment = (id, speaker, text) => {
    const newSegment = { ...currentSegment, speaker, text };
    const newSegments = data.transcript.segments.map((segment) => {
      if (segment.id === id) {
        return newSegment;
      }
      return segment;
    });

    const newRawText = newSegments.map((segment) => segment.text).join("");

    setTimeout(() => {
      dispatch(
        updateDataset({
          transcript: { text: newRawText, segments: newSegments },
        })
      );
    }, 0);
  };

  const handleSaveMultiple = (id, newSpeaker, text) => {
    const startIndex = data.transcript.segments.findIndex(
      (s) => s.id === selectedRange.start.id
    );
    const endIndex = selectedRange.end
      ? data.transcript.segments.findIndex((s) => s.id === selectedRange.end.id)
      : startIndex;

    const newSegments = data.transcript.segments.map((segment, index) => {
      if (index >= startIndex && index <= endIndex) {
        return { ...segment, speaker: newSpeaker };
      }
      return segment;
    });
    dispatch(updateDataset({ transcript: { segments: newSegments } }));
    setSelectedRange({ start: null, end: null });
  };

  const handleScrollToTranscript = () => {
    const element = document.getElementById(`segment-${currentPlayingSegment}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const handleDelete = () => {
    swal({
      title: "Are you sure?",
      text: "You're about to delete selected segment(s). Are you sure you want to do that?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        let idsToDelete = [];

        if (multipleSelected) {
          const startIndex = data.transcript.segments.findIndex(
            (s) => s.id === selectedRange.start.id
          );
          const endIndex = data.transcript.segments.findIndex(
            (s) => s.id === selectedRange.end.id
          );
          for (let i = startIndex; i <= endIndex; i++) {
            idsToDelete.push(data.transcript.segments[i].id);
          }
        } else {
          idsToDelete.push(currentSegment.id);
        }
        const newSegments = data.transcript.segments.filter(
          (segment) => !idsToDelete.includes(segment.id)
        );
        dispatch(updateDataset({ transcript: { segments: newSegments } }));
        setSelectedRange({ start: null, end: null });
        setCurrentSegment(null);
      }
    });
  };

  const groupedSegments = groupByConsecutiveSpeakers(data.transcript.segments);
  const multipleSelected = selectedRange.start && selectedRange.end;

  const [isInAside, setIsInAside] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0 && !isInAside) {
        setIsInAside(true);
      } else if (window.scrollY === 0 && isInAside) {
        setIsInAside(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isInAside]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.transcriptWrapper}>
      {data && data.transcript_status !== "COMPLETED" && (
        <div className={styles.searchReplacePanel} ref={searchReplaceRef}>
          <button 
            className={styles.searchReplaceToggle}
            onClick={() => setShowSearchReplace(!showSearchReplace)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 460 512" fill="currentColor"><path d="M220.6 130.3l-67.2 28.2V43.2L98.7 233.5l54.7-24.2v130.3l67.2-209.3zm-83.2-96.7l-1.3 4.7-15.2 52.9C80.6 106.7 52 145.8 52 191.5c0 52.3 34.3 95.9 83.4 105.5v53.6C57.5 340.1 0 272.4 0 191.6c0-80.5 59.8-147.2 137.4-158zm311.4 447.2c-11.2 11.2-23.1 12.3-28.6 10.5-5.4-1.8-27.1-19.9-60.4-44.4-33.3-24.6-33.6-35.7-43-56.7-9.4-20.9-30.4-42.6-57.5-52.4l-9.7-14.7c-24.7 16.9-53 26.9-81.3 28.7l2.1-6.6 15.9-49.5c46.5-11.9 80.9-54 80.9-104.2 0-54.5-38.4-102.1-96-107.1V32.3C254.4 37.4 320 106.8 320 191.6c0 33.6-11.2 64.7-29 90.4l14.6 9.6c9.8 27.1 31.5 48 52.4 57.4s32.2 9.7 56.8 43c24.6 33.2 42.7 54.9 44.5 60.3s.7 17.3-10.5 28.5zm-9.9-17.9c0-4.4-3.6-8-8-8s-8 3.6-8 8 3.6 8 8 8 8-3.6 8-8z"/></svg>
          </button>
          
          {showSearchReplace && (
            <div className={styles.searchReplaceControls}>
              <input
                type="text"
                placeholder="Search for..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <input
                type="text"
                placeholder="Replace with..."
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
              />
              <button 
                onClick={handleSearchReplace}
                disabled={!searchText}
              >
                Replace All
              </button>
            </div>
          )}
        </div>
        )}
        <div className={styles.transcript}>
          {!isInAside && (
            <div className={styles.mediaWrapper}>
              {isVideo ? (
                <div className={styles.mediaContainer}>
                  <video
                    ref={mediaRef}
                    className={styles.media}
                    id="mediaTranscript"
                    controls
                    src={`/media/${data.file_id}.${data.file_type}`}
                  />
                  <div
                    className={styles.button}
                    onClick={handleScrollToTranscript}
                  >
                    Click to scroll to this part of the transcript
                  </div>
                </div>
              ) : (
                <div className={styles.mediaContainer}>
                  <audio
                    ref={mediaRef}
                    className={styles.media}
                    id="mediaTranscript"
                    controls
                    src={`/media/${data.file_id}.${data.file_type}`}
                  />
                  <div
                    className={styles.button}
                    onClick={handleScrollToTranscript}
                  >
                    Click to scroll to this part of the transcript
                  </div>
                </div>
              )}
            </div>
          )}

          {groupedSegments.map((segments, index) => {
            const higlighted = hasEmptySpeaker(segments);
            return (
              <div className={styles.singleSegment} key={index}>
                <span
                  className={
                    higlighted
                      ? `${styles.counterMarker} ${styles.emptySpeaker}`
                      : styles.counterMarker
                  }
                >
                  {index + 1}
                </span>
                <SegmentGroup
                  segments={segments}
                  onSegmentClick={(segment, event) =>
                    handleSegmentClick(segment, event)
                  }
                  onEditClick={handleEditClick}
                  currentSegment={currentSegment}
                  selectedRange={selectedRange}
                  currentPlayingSegment={currentPlayingSegment}
                  stopEditing={stopEditing}
                  onSegmentDoubleClick={handleSegmentDoubleClick}
                />
              </div>
            );
          })}
          {showPopup && currentSegment != null && (
            <SegmentPopup
              segment={currentSegment}
              onClose={handleClosePopup}
              onSave={multipleSelected ? handleSaveMultiple : handleSaveSegment}
              multipleSelected={multipleSelected}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
      <aside className={styles.sidebar} ref={stickyAsideRef} style={{ maxHeight: window.innerWidth > 991 && `calc(100vh - ${headerHeight + 40}px` }}>
        {isInAside && (
          <div className={styles.mediaWrapper}>
            {isVideo ? (
              <div className={styles.mediaContainer}>
                <video
                  ref={mediaRef}
                  className={styles.media}
                  id="mediaTranscript"
                  controls
                  src={`/media/${data.file_id}.${data.file_type}`}
                />
                <div
                  className={styles.button}
                  onClick={handleScrollToTranscript}
                >
                  Click to scroll to this part of the transcript
                </div>
              </div>
            ) : (
              <div className={styles.mediaContainer}>
                <audio
                  ref={mediaRef}
                  className={styles.media}
                  id="mediaTranscript"
                  controls
                  src={`/media/${data.file_id}.${data.file_type}`}
                />
                <div
                  className={styles.button}
                  onClick={handleScrollToTranscript}
                >
                  Click to scroll to this part of the transcript
                </div>
              </div>
            )}
          </div>
        )}
        {data && data.moderator && data.moderator !== "" && (
          <TranscriptModerator />
        )}
        <SpeakerList />
        {data && data.transcript_status === "COMPLETED" ? (
          <>
          <ExtraButtons report={data.report_url} advisor={data.advisor} />
          <ExportTranscript />
          </>
        ) : null}
        <ScrollToTop />
      </aside>
    </div>
  );
}

export default Transcript;
